import type { S3File } from "@dune/types";
import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@dune/ui";
import React, { useState } from "react";
import { toast } from "sonner";
import { registerS3File } from "../../api/queries/files.queries";
import { S3FileInput } from "./S3FileInput";

interface S3FileWithId extends S3File {
	id?: string;
}

export interface DocumentTypeOption {
	value: string;
	label: string;
}

interface UploadDocumentModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	documentTypeOptions: DocumentTypeOption[];
	onUpload: (params: {
		fileId?: string;
		file?: S3File;
		documentType: string;
	}) => Promise<void>;
	isPending?: boolean;
	requiresFileRegistration?: boolean;
}

const UploadDocumentModal: React.FC<UploadDocumentModalProps> = ({
	open,
	onOpenChange,
	documentTypeOptions,
	onUpload,
	isPending = false,
	requiresFileRegistration = false,
}) => {
	const [selectedFile, setSelectedFile] = useState<S3FileWithId | null>(null);
	const [documentType, setDocumentType] = useState<string>("");
	const [isUploading, setIsUploading] = useState(false);

	const handleFileUpload = async (file: S3File) => {
		setSelectedFile(file);

		if (requiresFileRegistration) {
			try {
				setIsUploading(true);

				const registeredFile = await registerS3File({
					bucket: file.bucket,
					key: file.key,
					name: file.name,
					mimeType: file.type,
				});

				setSelectedFile({
					...file,
					id: registeredFile.id,
				});
			} catch (error) {
				console.error("File registration failed:", error);
				toast.error("Erreur lors de l'enregistrement du fichier");
				setSelectedFile(null);
			} finally {
				setIsUploading(false);
			}
		}
	};

	const handleUpload = async () => {
		if (!selectedFile || !documentType) {
			toast.error("Veuillez sélectionner un fichier et un type de document");
			return;
		}

		if (requiresFileRegistration && !selectedFile.id) {
			toast.error("Le fichier n'a pas été correctement enregistré");
			return;
		}

		try {
			await onUpload({
				fileId: selectedFile.id,
				file: selectedFile,
				documentType,
			});

			toast.success("Document uploadé avec succès");
			onOpenChange(false);
			setSelectedFile(null);
			setDocumentType("");
		} catch (error) {
			console.error("Upload failed:", error);
			toast.error("Erreur lors de l'envoi du document");
		}
	};

	const handleModalClose = (newOpen: boolean) => {
		if (!newOpen) {
			setSelectedFile(null);
			setDocumentType("");
		}
		onOpenChange(newOpen);
	};

	return (
		<Dialog open={open} onOpenChange={handleModalClose}>
			<DialogContent className="sm:max-w-[500px]">
				<DialogHeader>
					<DialogTitle>Ajouter un document</DialogTitle>
					<DialogDescription>
						Sélectionnez un fichier et choisissez le type de document
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					<div>
						<div className="text-sm font-medium mb-2">Type de document</div>
						<Select value={documentType} onValueChange={setDocumentType}>
							<SelectTrigger>
								<SelectValue placeholder="Sélectionnez un type de document" />
							</SelectTrigger>
							<SelectContent>
								{documentTypeOptions.map((option) => (
									<SelectItem key={option.value} value={option.value}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<div>
						<div className="text-sm font-medium mb-2">Fichier</div>
						<S3FileInput
							key={`upload-modal-${open}`}
							bucket="private"
							onUploadSuccess={handleFileUpload}
							hidePreviewButton={true}
							subText="Formats acceptés : PDF, DOCX, PNG, JPG (max. 10MB)"
						/>
					</div>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						Annuler
					</Button>
					<Button
						onClick={handleUpload}
						disabled={
							!selectedFile ||
							!documentType ||
							isPending ||
							isUploading ||
							(requiresFileRegistration && !selectedFile.id)
						}
						isLoading={isPending || isUploading}
					>
						Ajouter le document
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default UploadDocumentModal;
